import {
  IPayerResponse,
  PayeeTaxDocumentSharePermission,
  PayerPayeeStatus
} from "@wingspanhq/payments/dist/interfaces";
import type { onboardingApiSpec } from "@wingspanhq/wingspan-api-types";
import { CertifyTaxPayerInfo } from "../steps/CertifyTaxPayerInfo";
import { Tax1099Consent } from "../steps/CertifyTaxPayerInfo/Tax1099Consent";
import {
  OnboardingContext,
  OnboardingModuleCertifyTaxPayerInfo
} from "../types";
import {
  buildOnboardingStep,
  UseCreateOnboardingStep,
  useOnboardingStepQueryConfig
} from "../components/OnboardingFlowUtils";
import { useQueryAllPayerList } from "../../../query/payers/queries/useQueryAllPayerList";
import { useQueryAcknowledgement } from "../../../query/onboarding/queries/useQueryAcknowledgement";
import { FlowStep } from "../../../shared/components/FlowSwitch/types";

const getStatus = (
  payers?: IPayerResponse[],
  electronicTaxFormConsent?: onboardingApiSpec.operations["getAcknowledgement"]["responses"]["200"]["content"]["application/json"]
) => {
  const activePayers = payers?.filter(
    payer => payer.payeeOwnedData.status === PayerPayeeStatus.Active
  );

  const isTaxInfoSharedToPayers = activePayers?.every(
    payer =>
      payer.payeeOwnedData.shareTaxDocument ===
      PayeeTaxDocumentSharePermission.Allow
  );

  // Consenting to e-delivery is optional, so declining is a valid answer that must count
  // as answered - the e-delivery step has no other way to advance, since FlowSwitch nulls
  // out its onNext whenever the parent step has a result.Complete. But an acknowledgement
  // the user has never answered comes back from v3 as a synthesized "NotGiven" with no
  // version, so the status alone cannot tell "never asked" from "declined". Only a
  // recorded answer carries the version it was acknowledged against.
  const isElectronicTaxFormConsentAnswered =
    electronicTaxFormConsent?.acknowledgementStatus === "Given" ||
    !!electronicTaxFormConsent?.version;

  if (isTaxInfoSharedToPayers && isElectronicTaxFormConsentAnswered) {
    return "Complete";
  }

  return "None";
};

export const getCertifyTaxPayerInfoStepConfig = (
  _: OnboardingModuleCertifyTaxPayerInfo,
  __: OnboardingContext,
  payers: IPayerResponse[] = [],
  electronicTaxFormConsent?: onboardingApiSpec.operations["getAcknowledgement"]["responses"]["200"]["content"]["application/json"]
): FlowStep => {
  const slug = "taxpayer-info";

  const steps: FlowStep[] = [
    {
      title: "Certify taxpayer info",
      slug: "certify",
      component: CertifyTaxPayerInfo
    },
    {
      title: "Consent to e-delivery",
      slug: "e-delivery",
      component: Tax1099Consent
    }
  ];

  const status = getStatus(payers, electronicTaxFormConsent);

  return {
    title: "Certify taxpayer info",
    slug,
    steps,

    status,
    result: {
      Complete: {
        title: "You certified your taxpayer information",
        description:
          "You certified your taxpayer information is accurate. Click continue for the next step."
      }
    }
  };
};

export const useCertifyTaxPayerInfoStep: UseCreateOnboardingStep<
  OnboardingModuleCertifyTaxPayerInfo
> = modules => {
  const config = useOnboardingStepQueryConfig(
    modules,
    "certify_tax_payer_info"
  );
  const queryAllPayerList = useQueryAllPayerList(undefined, config);
  const queryElectronicTaxFormConsentAcknowledgement = useQueryAcknowledgement(
    "ElectronicTaxFormConsent",
    config
  );

  return (onboardingModule, contextValue) =>
    buildOnboardingStep({
      getConfig: getCertifyTaxPayerInfoStepConfig,
      module: onboardingModule,
      context: contextValue,
      queries: [queryAllPayerList, queryElectronicTaxFormConsentAcknowledgement]
    });
};
